import { ensureAnonSession, supabase } from '../lib/supabase'
import { EMPTY_DOC, parseDoc, type TranscriptDoc } from './transcriptDoc'
import type { TranscriptRevision } from '../types/portal'

/**
 * Stored transcripts.
 *
 * `transcript_versions` in Supabase is the ONLY source of truth. A transcript
 * is shared work: Portal uploads it, and anyone holding the project link may
 * correct it. Every save is an INSERT — nothing is ever updated or deleted, so
 * no state can be destroyed, which matches the append-only RLS in place
 * (SELECT and INSERT under has_project_access, no UPDATE, no DELETE).
 *
 * Ids are resolved by direct query every time, never from a cache. A cached
 * deliverable map goes stale the moment a deliverable is added, and a stale
 * map silently produced inserts that never happened.
 */

/** Who Portal is recorded as for the original upload. */
export const PORTAL_EDITOR = 'Portal'

export interface StoredTranscript {
  /** The latest saved text, struck wording excluded. */
  text: string
  /** The latest saved document, including its tracked deletions. */
  doc: TranscriptDoc
  /** Every saved state, oldest first — the original upload is the first. */
  revisions: TranscriptRevision[]
}

export type TranscriptLoad =
  | { status: 'ok'; transcript: StoredTranscript | null }
  | { status: 'error'; message: string }

export interface WriteResult {
  ok: boolean
  /** The stored row id, so the interface can adopt it. */
  id?: string
  /** The revision number the database assigned. */
  versionNumber?: number
  error?: string
}

const WRITE_ERROR = 'Transcript could not be saved.'

/** The numbering function is absent, rather than refusing the call. */
const MISSING_FUNCTION = new Set(['PGRST202', '42883'])

/** Postgres "duplicate key value violates unique constraint". */
const UNIQUE_VIOLATION = '23505'

function describe(error: unknown): string {
  const detail = error as { message?: string; code?: string } | null
  if (!detail) return ''
  return `${detail.message ?? String(error)}${detail.code ? ` (${detail.code})` : ''}`
}

function warn(what: string, error: unknown) {
  if (!error) return
  console.warn(`[supabase] ${what} failed: ${describe(error)}`)
}

/** The stored revision number, or 0 where a row predates the sequence. */
function numberOf(row: Record<string, unknown>): number {
  const value = Number(row.version_number)
  return Number.isFinite(value) ? value : 0
}

function revisionOf(row: Record<string, unknown>): TranscriptRevision {
  const created = String(row.created_at ?? '')
  const at = new Date(created).getTime()
  const text = String(row.content ?? '')
  return {
    id: String(row.id ?? ''),
    text,
    /* The stored document carries the tracked deletions. A row saved before
       tracked changes existed simply opens as plain paragraphs. */
    doc: parseDoc(row.marks, text),
    editorName: String(row.editor_name ?? PORTAL_EDITOR),
    at: Number.isNaN(at) ? Date.now() : at,
  }
}

/** A row with no content is not a transcript; it must not read as an empty one. */
function usable(rows: Record<string, unknown>[]): Record<string, unknown>[] {
  return rows.filter((row) => String(row.content ?? '').trim().length > 0)
}

/* ------------------------------------------------------------------ ids ---- */

interface Ids {
  projectId: string
  deliverableId: string
}

/**
 * The real project and deliverable ids for a slug and deliverable key.
 *
 * Resolved live on every call. Both queries run under the caller's own
 * session, so a client on a project link and signed-in staff each get exactly
 * what their RLS allows and nothing more.
 */
async function resolveIds(
  projectSlug: string,
  deliverableKey: string,
): Promise<{ ids: Ids | null; error?: string }> {
  if (!supabase || !projectSlug || !deliverableKey) return { ids: null }

  const { data: project, error: projectError } = await supabase
    .from('projects')
    .select('id')
    .eq('slug', projectSlug)
    .maybeSingle()

  if (projectError) {
    warn('transcript project lookup', projectError)
    return { ids: null, error: describe(projectError) }
  }
  if (!project?.id) return { ids: null, error: 'Project not found for this link.' }

  const { data: deliverable, error: deliverableError } = await supabase
    .from('deliverables')
    .select('id')
    .eq('project_id', project.id)
    .eq('deliverable_key', deliverableKey)
    .maybeSingle()

  if (deliverableError) {
    warn('transcript deliverable lookup', deliverableError)
    return { ids: null, error: describe(deliverableError) }
  }
  if (!deliverable?.id) return { ids: null, error: 'Deliverable not found in this project.' }

  return { ids: { projectId: String(project.id), deliverableId: String(deliverable.id) } }
}

/* ----------------------------------------------------------------- reads ---- */

/**
 * The authoritative transcript for one deliverable: every saved state ordered
 * by `created_at`, with the newest as the current text.
 *
 * This is what the client transcript page reads. It does not depend on array
 * position, React state, or how the project happened to be mapped — only on
 * stored rows and their stored order. A failure is reported as a failure and
 * never silently becomes an empty transcript.
 */
export async function fetchDeliverableTranscript(
  projectSlug: string,
  deliverableKey: string,
): Promise<TranscriptLoad> {
  if (!supabase) return { status: 'ok', transcript: null }

  const { ids, error } = await resolveIds(projectSlug, deliverableKey)
  if (error) return { status: 'error', message: error }
  if (!ids) return { status: 'ok', transcript: null }

  /* Ordered by the stored revision number, with created_at deciding any row
     that predates the sequence. Never by array position or React state. */
  const { data, error: readError } = await supabase
    .from('transcript_versions')
    .select('id, content, marks, editor_name, created_at, version_number')
    .eq('project_id', ids.projectId)
    .eq('deliverable_id', ids.deliverableId)
    .order('version_number', { ascending: true, nullsFirst: true })
    .order('created_at', { ascending: true })

  if (readError) {
    warn('transcript read', readError)
    return { status: 'error', message: describe(readError) }
  }

  const rows = usable((data ?? []) as Record<string, unknown>[])
  if (rows.length === 0) return { status: 'ok', transcript: null }

  const revisions = rows.map(revisionOf)
  const current = revisions[revisions.length - 1]
  return {
    status: 'ok',
    transcript: { text: current.text, doc: current.doc, revisions },
  }
}

/**
 * Every stored transcript for a project, by the real project id — used while
 * the project and its deliverables are loading, so both the studio side and
 * the client link start from the same stored content.
 *
 * `keyOf` maps each database deliverable id to the key the interface uses.
 */
export async function fetchTranscriptsForProject(
  projectId: string,
  keyOf: Map<string, string>,
): Promise<Map<string, StoredTranscript>> {
  const found = new Map<string, StoredTranscript>()
  if (!supabase || !projectId) return found

  const { data, error } = await supabase
    .from('transcript_versions')
    .select('id, deliverable_id, content, marks, editor_name, created_at, version_number')
    .eq('project_id', projectId)
    .order('version_number', { ascending: true, nullsFirst: true })
    .order('created_at', { ascending: true })

  warn('transcript fetch', error)
  if (!data?.length) return found

  for (const row of usable(data as Record<string, unknown>[])) {
    const key = keyOf.get(String(row.deliverable_id))
    if (!key) continue
    const existing = found.get(key) ?? { text: '', doc: EMPTY_DOC, revisions: [] }
    existing.revisions.push(revisionOf(row))
    found.set(key, existing)
  }

  /* Stored order decides the current text — never insertion order in React. */
  for (const entry of found.values()) {
    const current = entry.revisions[entry.revisions.length - 1]
    entry.text = current?.text ?? ''
    entry.doc = current?.doc ?? EMPTY_DOC
  }

  return found
}

/* ---------------------------------------------------------------- writes ---- */

/**
 * Records one saved state.
 *
 * Append-only, and the single write path for both Portal's upload and a
 * client's correction. Returns whether the database accepted it, so nothing in
 * the interface can report a save that did not happen.
 *
 * `user_id` is whoever is signed in: the Portal staff member on the studio
 * side, the anonymous project session on the client side.
 */
export async function insertTranscriptRevision(input: {
  projectSlug: string
  deliverableKey: string
  text: string
  editorName: string
  /** The document with its tracked deletions. Omitted for a plain upload. */
  doc?: TranscriptDoc
}): Promise<WriteResult> {
  if (!supabase) return { ok: false, error: 'No database connection.' }

  if (!input.text.trim()) return { ok: false, error: 'The transcript is empty.' }

  const { ids, error } = await resolveIds(input.projectSlug, input.deliverableKey)
  if (!ids) return { ok: false, error: error ?? WRITE_ERROR }

  const userId = await ensureAnonSession()

  /**
   * The revision number is generated by the database, which serialises
   * concurrent saves for the same deliverable and holds a unique index on
   * (deliverable_id, version_number). Two reviewers saving seconds apart
   * therefore cannot land on the same number.
   */
  const { data, error: rpcError } = await supabase
    .rpc('insert_transcript_revision', {
      p_project_id: ids.projectId,
      p_deliverable_id: ids.deliverableId,
      p_content: input.text,
      p_editor_name: input.editorName,
      p_marks: input.doc ?? null,
    })
    .select('id, version_number')
    .maybeSingle()

  if (!rpcError) {
    return {
      ok: true,
      id: (data?.id as string) ?? undefined,
      versionNumber: data ? numberOf(data as Record<string, unknown>) : undefined,
    }
  }

  /**
   * Only a genuinely absent function falls back. Anything else — a refusal,
   * an empty transcript, a missing project — is reported as it stands rather
   * than retried against a path with weaker guarantees.
   */
  if (!MISSING_FUNCTION.has((rpcError as { code?: string }).code ?? '')) {
    warn('transcript insert', rpcError)
    return { ok: false, error: `${WRITE_ERROR} ${describe(rpcError)}` }
  }

  console.warn(
    '[supabase] insert_transcript_revision is not installed — run supabase/migrations/transcript_revision_numbering.sql. Falling back to a numbered insert.',
  )

  return insertNumberedRevision({
    projectId: ids.projectId,
    deliverableId: ids.deliverableId,
    userId,
    text: input.text,
    editorName: input.editorName,
    doc: input.doc,
  })
}

/**
 * The fallback used only while the numbering function is not installed.
 *
 * It reads the highest stored number and inserts the next one, and retries on
 * a unique-index collision so a simultaneous save cannot reuse a number.
 * Still append-only, and still supplies version_number on every insert.
 */
async function insertNumberedRevision(input: {
  projectId: string
  deliverableId: string
  userId: string | null
  text: string
  editorName: string
  doc?: TranscriptDoc
}): Promise<WriteResult> {
  if (!supabase) return { ok: false, error: 'No database connection.' }

  for (let attempt = 0; attempt < 3; attempt += 1) {
    const { data: highest, error: readError } = await supabase
      .from('transcript_versions')
      .select('version_number')
      .eq('deliverable_id', input.deliverableId)
      .order('version_number', { ascending: false, nullsFirst: false })
      .limit(1)
      .maybeSingle()

    if (readError) {
      warn('transcript revision number', readError)
      return { ok: false, error: `${WRITE_ERROR} ${describe(readError)}` }
    }

    const next = (highest ? numberOf(highest as Record<string, unknown>) : 0) + 1

    const { data, error: insertError } = await supabase
      .from('transcript_versions')
      .insert({
        project_id: input.projectId,
        deliverable_id: input.deliverableId,
        version_number: next,
        user_id: input.userId,
        content: input.text,
        marks: input.doc ?? null,
        editor_name: input.editorName,
      })
      .select('id, version_number')
      .single()

    if (!insertError) {
      return {
        ok: true,
        id: (data?.id as string) ?? undefined,
        versionNumber: data ? numberOf(data as Record<string, unknown>) : next,
      }
    }

    /* Someone else took this number in the meantime; read again and retry. */
    if ((insertError as { code?: string }).code === UNIQUE_VIOLATION) continue

    warn('transcript insert', insertError)
    return { ok: false, error: `${WRITE_ERROR} ${describe(insertError)}` }
  }

  return {
    ok: false,
    error: `${WRITE_ERROR} Another save was in progress. Try again.`,
  }
}

/** Recorded against the deliverable so Portal knows which document this came from. */
export const PORTAL_UPLOAD_EDITOR = 'Portal upload'
